import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "./maintenance-mutation";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import type { PartPaymentInput } from "./parts-types";

export type PaymentEvidence = {
  id: string; method: string; total: string; occurredAt: string; reference: string;
  status: "posted" | "voided"; ledgerHref: string | null;
  voided: { at: string; by: string | { id?: string; name: string }; reason: string; replacementPaymentId: string | null } | null;
};
export type Reconciliation = {
  checkedAt: string | null; status: "reconciled" | "differences" | "unchecked" | "failed"; differenceCount: number;
  differences: { key: string; branchId: string; businessDate: string; method: string; question: string; questionAr: string; jobId: string | null; paymentId: string | null; entryId: string | null }[];
  totals: unknown[];
};
export type JournalEvidence = { entry: {
  id: string; method: string; amount: string; direction: string; businessDate: string; branchName: string;
  reference: string; occurredAt: string; sourceType: string; sourceId: string; jobId: string | null; reversesEntryId: string | null;
}; jobHref: string | null };
export function maintenancePaymentHref(jobId: string, paymentId?: string | null, journalId?: string | null) {
  const params = new URLSearchParams({ job: jobId });
  if (paymentId) params.set("payment", paymentId);
  if (journalId) params.set("journal", journalId);
  return `/maintenance/jobs/all?${params}`;
}
function useEvidenceQuery<T>(path: string, enabled = true) {
  const { user, currentWorkspace } = useAuth();
  return useQuery<T>({
    queryKey: ["maintenance-evidence", currentWorkspace?.id, user?.id, path],
    enabled: enabled && !!user && !!currentWorkspace,
    queryFn: async () => (await apiRequest("GET", path)).json(),
  });
}
export function usePaymentEvidence(jobId: string) {
  const query = useEvidenceQuery<{canCorrect: boolean; payments: PaymentEvidence[]; journalEntry?: object}>(`/api/maintenance/jobs/${encodeURIComponent(jobId)}/payment-evidence`);
  const client = useQueryClient();
  const change = useMutation<unknown, Error, {paymentId: string; action: "void" | "correct"; body: {requestId: string; reason: string; occurredAt: string; replacement?: PartPaymentInput}}>({
    mutationFn: async ({paymentId, action, body}) => {
      if (!query.data?.canCorrect) throw new Error("Permission denied.");
      return (await apiRequest("POST", `/api/maintenance/jobs/${encodeURIComponent(jobId)}/payments/${encodeURIComponent(paymentId)}/${action}`, body)).json();
    },
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({queryKey: ["maintenance-evidence"]}),
        client.invalidateQueries({queryKey: ["/api/maintenance/jobs/bootstrap"]}),
        client.invalidateQueries({queryKey: ["/api/maintenance/parts/bootstrap"]}),
        client.invalidateQueries({queryKey: ["branch-cash"]}),
        client.invalidateQueries({queryKey: ["/api/maintenance/brand-comparisons"]}),
        ...["suppliers", "supplier-balance", "supplier-payables", "supplier-statement", "/api/suppliers/payables/summary"].map(key => client.invalidateQueries({queryKey: [key]})),
      ]);
    },
  });
  return {...query, change};
}
export function useMaintenanceJournal(id: string | null) {
  return useEvidenceQuery<JournalEvidence>(`/api/maintenance/journal/${encodeURIComponent(id || "")}`, !!id);
}
export function useMaintenanceReconciliation(branchId: string) {
  // Always a concrete branch: scoped screens never request all-branch evidence.
  return useEvidenceQuery<Reconciliation>(`/api/maintenance/reconciliation?branch=${encodeURIComponent(branchId)}`, !!branchId);
}
